"use client"
import { createContext, useContext } from "react"

export type CmsLink = { label: string; href: string; newTab?: boolean }

/**
 * The shape the header and footer consume. Everything is optional: when a field is
 * missing the component keeps its own built-in copy, so the site never renders a
 * blank nav or an empty footer because a CMS field was cleared.
 */
export type SiteSettingsValue = {
  navItems?: CmsLink[]
  navCtaLabel?: string
  navCtaHref?: string
  footerTagline?: string
  footerNavItems?: CmsLink[]
  footerLegalLinks?: CmsLink[]
  footerCtaLabel?: string
  footerCtaHref?: string
  footerMarketsNote?: string
  footerPaymentNote?: string
  phone?: string
  phoneLink?: string
  emailPrimary?: string
  emailSupport?: string
  whatsappLabel?: string
  whatsappHref?: string
  socialLinks?: CmsLink[]
  bookingSrc?: string
  bookingId?: string
  bookingTitle?: string
}

const SiteSettingsContext = createContext<SiteSettingsValue>({})

export function SiteSettingsProvider({
  value,
  children,
}: {
  value: SiteSettingsValue
  children: React.ReactNode
}) {
  return <SiteSettingsContext.Provider value={value}>{children}</SiteSettingsContext.Provider>
}

export function useSiteSettings(): SiteSettingsValue {
  return useContext(SiteSettingsContext)
}

/** Read a text setting, falling back to the built-in copy. */
export function useSetting(key: keyof SiteSettingsValue, fallback: string): string {
  const v = useSiteSettings()[key]
  return typeof v === "string" && v.trim() ? v : fallback
}

/** Read a link list, falling back to the built-in list when unset or empty. */
export function useLinks(key: keyof SiteSettingsValue, fallback: CmsLink[]): CmsLink[] {
  const v = useSiteSettings()[key]
  return Array.isArray(v) && v.length > 0 ? (v as CmsLink[]) : fallback
}
